import type {
  DEFAULT_TRANSLATION_LANGUAGE_CODE,
  RecursivePartial,
} from 'src/helpers/combine-translation-data';
import type { FooterContent } from 'src/models/cms/footer-content';
import type { GlobalSharedContent } from 'src/models/global/global-shared-content';

export type LocalisedTemplate<T> = {
  [DEFAULT_TRANSLATION_LANGUAGE_CODE]: T;
  localised: RecursivePartial<T> | null;
};

export type GlobalHeaderContent = {
  headerMenuItems?: Queries.HeaderLevelOneMenuItem[];
  headerSecondaryUpperMenu?: Queries.HeaderSecondaryUpperMenuItem[];
  headerPromoRibbon?: Queries.HeaderPromoRibbon;
  headerRecentlyViewedClubRibbon?: Queries.GlobalHeaderRecentlyViewedClubRibbon;
  headerButton?: Queries.RedesignButton;
  headerButtonSecondary?: Queries.RedesignButton;
  headerOtherText?: Queries.HeaderOtherText;
  searchSettings: Queries.HeaderSearchSettings;
  linkDropdownCta?: Queries.LinkDropdownCta;
};

export type GlobalCmsRecord = {
  sharedContent: GlobalSharedContent;
  headerContent: GlobalHeaderContent;
  footerContent: FooterContent;
  appPromoTemplateContent: LocalisedTemplate<Queries.TemplateAppPromo>[];
  blogTemplateContent: LocalisedTemplate<Queries.TemplateBlog>[];
  membershipTemplateContent: LocalisedTemplate<Queries.TemplateMembershipContent>[];
  whyJoinListTemplateContent: LocalisedTemplate<Queries.TemplateWhyJoinList>[];
  productBannerImageWithTextTemplateContent: LocalisedTemplate<any>[];
};

// Some pages do not require this information. This tripwire is a sanity check for page queries.
const createMissingTemplateContentTripwire = <T,>(): T[] =>
  new Proxy([] as T[], {
    get(target, prop, receiver) {
      if (typeof prop === 'symbol' || prop === 'length') {
        return Reflect.get(target, prop, receiver) as unknown;
      }
      throw new Error('Global template content missing from page query.');
    },
  });

type GlobalCmsPageQueryData = {
  globalSharedContent?: { nodes: readonly { childGlobalJson: GlobalSharedContent | null }[] };
  globalHeaderContent?: { nodes: readonly { childGlobalJson: GlobalHeaderContent | null }[] };
  globalFooterContent?: { nodes: readonly { childGlobalJson: FooterContent | null }[] };
  appPromoTemplateContent?: {
    nodes: readonly {
      childTemplateAppPromoJson: GlobalCmsRecord['appPromoTemplateContent'][number] | null;
    }[];
  };
  blogTemplateContent?: {
    nodes: readonly {
      childTemplateBlogJson: GlobalCmsRecord['blogTemplateContent'][number] | null;
    }[];
  };
  membershipTemplateContent?: {
    nodes: readonly {
      childTemplateMembershipContentJson:
        | GlobalCmsRecord['membershipTemplateContent'][number]
        | null;
    }[];
  };
  whyJoinListTemplateContent?: {
    nodes: readonly {
      childTemplateWhyJoinListJson: GlobalCmsRecord['whyJoinListTemplateContent'][number] | null;
    }[];
  };
  productBannerImageWithTextTemplateContent?: {
    nodes: readonly {
      childTemplateProductBannerImageWithTextJson:
        | GlobalCmsRecord['productBannerImageWithTextTemplateContent'][number]
        | null;
    }[];
  };
};

export const unwrapGlobalCmsRecordFromPageQuery = (data: unknown): GlobalCmsRecord => {
  const pageData = data as GlobalCmsPageQueryData;

  return {
    sharedContent: pageData.globalSharedContent!.nodes[0].childGlobalJson!,
    headerContent: pageData.globalHeaderContent!.nodes[0].childGlobalJson!,
    footerContent: pageData.globalFooterContent!.nodes[0].childGlobalJson!,
    appPromoTemplateContent: pageData.appPromoTemplateContent
      ? pageData.appPromoTemplateContent.nodes.map((node) => node.childTemplateAppPromoJson!)
      : createMissingTemplateContentTripwire<GlobalCmsRecord['appPromoTemplateContent'][number]>(),
    blogTemplateContent: pageData.blogTemplateContent
      ? pageData.blogTemplateContent.nodes.map((node) => node.childTemplateBlogJson!)
      : createMissingTemplateContentTripwire<GlobalCmsRecord['blogTemplateContent'][number]>(),
    membershipTemplateContent: pageData.membershipTemplateContent
      ? pageData.membershipTemplateContent.nodes.map(
          (node) => node.childTemplateMembershipContentJson!
        )
      : createMissingTemplateContentTripwire<
          GlobalCmsRecord['membershipTemplateContent'][number]
        >(),
    whyJoinListTemplateContent: pageData.whyJoinListTemplateContent
      ? pageData.whyJoinListTemplateContent.nodes.map((node) => node.childTemplateWhyJoinListJson!)
      : createMissingTemplateContentTripwire<
          GlobalCmsRecord['whyJoinListTemplateContent'][number]
        >(),
    productBannerImageWithTextTemplateContent: pageData.productBannerImageWithTextTemplateContent
      ? pageData.productBannerImageWithTextTemplateContent.nodes.map(
          (node) => node.childTemplateProductBannerImageWithTextJson!
        )
      : createMissingTemplateContentTripwire<
          GlobalCmsRecord['productBannerImageWithTextTemplateContent'][number]
        >(),
  };
};
